import { asToken, joinSlots, routerValue, splitSlots, type UrlValue } from "./url-slots";

// Ability Points (CONTEXT.md: Ability Points, Ability Level; ADR 0015) — the third per-hull
// lever, and its URL grammar. Modelled on lib/level.ts and lib/loadout.ts and pure in the
// same way: no dataset import, every join handed in, so every rule below is tested over
// plain values rather than fixtures.
//
// The game sells Ability Levels for Ability Points; a hull's Level only gates which may be
// bought and how many points there are to buy them with (read in game 2026-09-09 — a level-2
// Vulkoras Desolator with nothing spent bombs at the bare figure). So an allocation belongs
// to ONE hull in one place, an entity page or one comparison column, and like a Loadout and
// a Level it does NOT ride unit-level links: `?points=` is deliberately absent from
// lib/frame.ts's SerializedFrame, and that absence is what makes clicking to another hull
// start it with nothing spent.

/** An allocation: the Ability Level bought on each of the hull's level-driven abilities, in
 * the game's own ability order, zero being unspent. Positional rather than keyed because the
 * URL is positional and the order is the game's; the ids are resolved against the hull's own
 * list wherever the hull is known. */
export type Allocation = readonly number[];

/** Nothing spent — the Base frame's allocation, and what every bare link means. */
export const NO_POINTS: Allocation = [];

/** What the rules need to know about one of a hull's level-driven abilities: the hull level
 * each of its Ability Levels requires (the game's `min_required_unit_levels`, entry i the
 * hull level Ability Level i+1 needs, 0 meaning none). The rest of the ability rides along
 * untouched (pointedAbilities is generic), so a caller that needs its name or icon reads it
 * off the same object rather than joining the list back to the hull by id. */
export interface PointedAbility {
  id: string;
  requiredUnitLevels: readonly number[];
}

/** The hull's abilities the lever owns, in the game's order — the positions `?points=`
 * counts. Exactly the level-driven ones: a research-levelled ability keeps taking its Ability
 * Level from the Tech Set and an item-granted one from the Loadout (ADR 0015 decision 6), so
 * neither is in the pool and neither has an entry here.
 *
 * Research wins where an ability carries BOTH gate lists, because that is the order the fold
 * dispatches in (compute's abilityLevelStateUnder reads `levelPrerequisites` first). No
 * ability in 2.0.3.0 carries both — measured 2026-09-16: 132 carry `requiredUnitLevels`, 48
 * carry `levelPrerequisites`, none both — so this arm is unreachable today. It is written
 * anyway because the alternative is the silent kind of gap: without it a patch that shipped
 * one ability with both fields would let the panel spend points on it and the fold ignore
 * them, with no error anywhere. */
export function pointedAbilities<
  T extends {
    id: string;
    requiredUnitLevels?: readonly number[];
    levelPrerequisites?: unknown;
  },
>(abilities: readonly T[] | undefined): (T & PointedAbility)[] {
  const pointed: (T & PointedAbility)[] = [];
  for (const ability of abilities ?? []) {
    if (ability.levelPrerequisites !== undefined) continue;
    const required = ability.requiredUnitLevels;
    if (required === undefined || required.length === 0) continue;
    pointed.push({ ...ability, requiredUnitLevels: required });
  }
  return pointed;
}

/** How many Ability Points a hull has at a Level.
 *
 * **ASSUMED, and this function is the whole of the assumption** (ADR 0015 decision 3, its one
 * open gate): one point per Level, N at Level N, one from build. The game's entity files do
 * not state the rule, and the gates do not settle it either (measured 2026-09-16 over the 33
 * levelled hulls: 21 offer exactly ten Ability Levels against ten Levels, which one per Level
 * would exactly exhaust; the other 12 offer seventeen, which no pool of ten can buy out). One
 * per Level is the simplest rule the first group is consistent with, and it is nothing more
 * than that. Until it is read in game the panel says so in an Assumption Caption. If the game
 * gives a different count, this line changes and nothing else does: the fold arithmetic under
 * a bought Ability Level is the verified one, and only how many may be bought is assumed. */
export function abilityPointPool(level: number): number {
  return level;
}

/** The highest Ability Level one ability may reach at a hull Level — the gate. The entries of
 * `min_required_unit_levels` are non-decreasing, so it is the count of them the hull has
 * reached: 0 while the ability is locked outright, which is the 33 abilities that name a
 * level of 5 or 9. */
export function abilityLevelGate(requiredUnitLevels: readonly number[], level: number): number {
  let gate = 0;
  for (const [index, need] of requiredUnitLevels.entries()) {
    if (need <= level) gate = index + 1;
  }
  return gate;
}

/** The Ability Level bought at one position, zero for a position the allocation is shorter
 * than: a shorter list pads with zeros, so a link that names the first ability alone means
 * the same as one that spells every trailing zero out. */
export function allocationAt(allocation: Allocation, index: number): number {
  return allocation[index] ?? 0;
}

/** The Ability Points an allocation has spent — one point per Ability Level. */
export function pointsSpent(allocation: Allocation): number {
  return allocation.reduce((total, count) => total + count, 0);
}

/** Whether anything at all is spent, which is what keeps `?points=` out of a bare link. */
export function anythingSpent(allocation: Allocation | undefined): boolean {
  return (allocation ?? NO_POINTS).some((count) => count > 0);
}

/** An allocation over exactly these abilities' positions, padded with zeros and with any
 * trailing surplus dropped. */
function padAllocation(allocation: Allocation, length: number): number[] {
  return Array.from({ length }, (_, index) => allocationAt(allocation, index));
}

/** An allocation with its trailing zeros dropped — the canonical form, since a position the
 * list does not reach is unspent anyway. The URL is written from it, so `0,0,1,0` and
 * `0,0,1` are one value and a bare link stays bare. Not the trim (trimAllocation): this
 * changes how an allocation is SPELLED, never what it spends. */
function canonicalAllocation(allocation: Allocation): number[] {
  const counts = [...allocation];
  while (counts.length > 0 && counts[counts.length - 1] === 0) counts.pop();
  return counts;
}

/** Whether an allocation is one the game could reach (ADR 0015 decision 2): it names no more
 * abilities than the hull has, every count is a whole number of Ability Levels no higher than
 * that ability's gate at this Level, and the total does not exceed the pool. This is the one
 * legality rule; the URL, the fold and the controls all ask it rather than restating it. */
export function isLegalAllocation(
  allocation: Allocation,
  abilities: readonly PointedAbility[],
  level: number,
): boolean {
  if (allocation.length > abilities.length) return false;
  if (allocation.some((count) => !Number.isInteger(count) || count < 0)) return false;
  if (pointsSpent(allocation) > abilityPointPool(level)) return false;
  return abilities.every(
    (ability, index) =>
      allocationAt(allocation, index) <= abilityLevelGate(ability.requiredUnitLevels, level),
  );
}

/** Why a click did not happen. Refused with the reason rather than clamped: a control that
 * silently does something other than what was clicked is one a reader cannot learn.
 *
 *  - `gate` — the hull's Level does not yet permit that Ability Level ("Requires Level N"),
 *    or the ability offers no such position at all.
 *  - `pool` — the Ability Points left cannot reach it (the game's "No ability points
 *    available"). */
export type SpendRefusal = "gate" | "pool";

export interface AllocationChange {
  allocation: Allocation;
  /** Present when the change did not happen; the allocation is then exactly what it was. */
  refused?: SpendRefusal;
}

/** One ability moved to the Ability Level a reader clicked, or the refusal and its reason
 * (Issue 62). The control offers every position 0..N at once rather than a pair of step
 * buttons, so one click can cross several Ability Levels; it lands on the position or moves
 * nothing. Clicking a lower position unspends down to it and position 0 clears the ability,
 * which is how a reader moves points around (PRD story 8).
 *
 * The gate is asked before the pool, so a position the Level does not permit is refused as
 * `gate` however many points are left: "Requires Level N" is the reason that says what to
 * change, and "No ability points available" on such a position would send the reader to free
 * points that cannot help (review 2026-09-10 — the earlier one-step-at-a-time walk named the
 * pool first whenever it ran dry before reaching the gate). */
export function setAbilityLevel(
  allocation: Allocation,
  index: number,
  position: number,
  abilities: readonly PointedAbility[],
  level: number,
): AllocationChange {
  const ability = abilities[index];
  if (ability === undefined) return { allocation };
  const current = allocationAt(allocation, index);
  if (position === current) return { allocation };
  if (position < 0 || position > abilityLevelGate(ability.requiredUnitLevels, level)) {
    return { allocation, refused: "gate" };
  }
  if (pointsSpent(allocation) - current + position > abilityPointPool(level)) {
    return { allocation, refused: "pool" };
  }
  const next = padAllocation(allocation, abilities.length);
  next[index] = position;
  return { allocation: canonicalAllocation(next) };
}

/** What the control draws on one Ability Level position (Issue 62):
 *
 *  - `in-force` — the Ability Level the allocation has bought on this ability, marked; on an
 *    unspent ability that is position 0;
 *  - `available` — one click sets it, whether that spends up to it or unspends down to it;
 *  - `gated` — the hull's Level does not permit it ("Requires Level N");
 *  - `unaffordable` — the pool cannot reach it (the game's "No ability points available").
 *
 * Read off setAbilityLevel's own refusal rather than restated, so a position the control
 * disables is exactly a position the click would refuse. */
export type PositionState = "in-force" | "available" | "gated" | "unaffordable";

export function positionState(
  allocation: Allocation,
  index: number,
  position: number,
  abilities: readonly PointedAbility[],
  level: number,
): PositionState {
  if (position === allocationAt(allocation, index)) return "in-force";
  const change = setAbilityLevel(allocation, index, position, abilities, level);
  if (change.refused === "gate") return "gated";
  if (change.refused === "pool") return "unaffordable";
  return "available";
}

/** The hull level one Ability Level position requires — the game's own entry for it, which
 * is what a gated position names ("Requires Level 5"). Undefined for a position the ability
 * does not offer, so a caller can never print a requirement the game does not state. */
export function abilityLevelRequirement(
  requiredUnitLevels: readonly number[],
  position: number,
): number | undefined {
  return requiredUnitLevels[position - 1];
}

/** One ability the trim moved: its position in the game's order, the Ability Level it held,
 * and the one it holds now (0 being unspent). Positions rather than names, because this
 * module never sees a hull — the words are composed where the hull is known
 * (lib/frame-labels.ts, abilityPointsTrimNote). */
export interface AllocationTrim {
  index: number;
  from: number;
  to: number;
}

/** What the trim did: the legal allocation, and every ability it moved in the game's order.
 * `trimmed` empty means the allocation was already one the game could reach, and nothing is
 * said out loud. */
export interface TrimmedAllocation {
  allocation: Allocation;
  trimmed: readonly AllocationTrim[];
}

/** The nearest allocation the game could reach at this Level, and what that cost (ADR 0015
 * decision 2). Deterministic, in the two steps the ADR names and in that order:
 *
 *  1. every Ability Level above its gate drops to the highest the Level permits — the reader
 *     cannot hold an Ability Level the ship has not earned, and a locked ability's gate is
 *     zero, so it empties;
 *  2. then, while the total still exceeds the pool, ONE point comes off the last ability in
 *     the game's order that still has any, one at a time — the front of the order is where
 *     the game opens abilities and where a reader's first spends go, so the last is the one
 *     they lose. Repeating the single step rather than emptying an ability outright is what
 *     makes the same Level drop always land on the same allocation.
 *
 * Refusing the Level change was the alternative and makes the Level control feel broken; this
 * is the Tech Set's own rule for items that needed unticked research, applied to the next
 * lever. Raising a Level never reaches step 2 and never trims, because a bigger pool and
 * wider gates cannot make a legal allocation illegal. */
export function trimAllocation(
  allocation: Allocation,
  abilities: readonly PointedAbility[],
  level: number,
): TrimmedAllocation {
  const before = padAllocation(allocation, abilities.length);
  const counts = [...before];
  for (const [index, ability] of abilities.entries()) {
    const gate = abilityLevelGate(ability.requiredUnitLevels, level);
    const count = counts[index] ?? 0;
    if (count > gate) counts[index] = gate;
  }
  const pool = abilityPointPool(level);
  while (pointsSpent(counts) > pool) {
    const last = counts.findLastIndex((count) => count > 0);
    // Unreachable while the pool is at least zero: a total above it has a point to take.
    if (last < 0) break;
    counts[last] = (counts[last] ?? 0) - 1;
  }
  const trimmed: AllocationTrim[] = [];
  for (const [index, from] of before.entries()) {
    const to = counts[index] ?? 0;
    if (to !== from) trimmed.push({ index, from, to });
  }
  return { allocation: canonicalAllocation(counts), trimmed };
}

// --- The URL grammar --------------------------------------------------------------------

/** A `?points=` value as it can arrive (lib/url-slots.ts, UrlValue): a one-ability
 * allocation is a bare number to the router, every other spelling a string. */
export type PointsValue = UrlValue;

/** One `?points=` slot: the counts in the hull's ability order, comma-separated. Honoured
 * only as plain whole numbers — " 1 ", "1e0", "0x1", "1.5" and "two" each name a ship the
 * game cannot field — so one bad token falls back the WHOLE slot to nothing spent and hands
 * the value back for the notice to quote. Legality against a hull and a Level is a separate
 * question and a separate rule (isLegalAllocation): this only settles the spelling. */
export function parsePointsSlot(value: PointsValue | undefined): {
  points: Allocation;
  unknown?: string;
} {
  const token = asToken(value);
  if (token === undefined || token === "") return { points: NO_POINTS };
  const counts: number[] = [];
  for (const part of token.split(",")) {
    if (!/^\d+$/.test(part)) return { points: NO_POINTS, unknown: token };
    counts.push(Number(part));
  }
  return { points: canonicalAllocation(counts) };
}

/** The allocation as its one slot value, or undefined when nothing is spent so a bare link
 * stays canonical — the rule Base, an uncorrupted frame and level 1 already follow. Spelled
 * in the router's own way (routerValue): a one-position allocation is a NUMBER, read on the
 * served build 2026-09-10 as `?points=%222%22` before it was. */
export function serializePoints(allocation: Allocation | undefined): PointsValue | undefined {
  if (allocation === undefined) return undefined;
  const counts = canonicalAllocation(allocation);
  return counts.length === 0 ? undefined : routerValue(counts.join(","));
}

// The per-column layer (ADR 0011, lib/url-slots.ts): the one coercion this key adds is
// PointsValue → string, so a number arriving from the router splits like any other slot.

export const splitPointsSlots = (value: PointsValue | undefined): string[] =>
  splitSlots(asToken(value));

/** The columns' slots as the one `?points=` value, joined with trailing defaults trimmed and
 * spelled the router's way (routerValue), exactly as lib/level.ts joins `?level=`. */
export function joinPointsValues(
  slots: readonly (PointsValue | undefined)[],
): PointsValue | undefined {
  return routerValue(joinSlots(slots.map(asToken)));
}
